import Layout from "../Layout/Layout";
import { JsonLDData } from "react-safe-json-ld";
import { Link } from "react-router-dom";
import React from "react";

const CALENDLY_URL =
  "https://calendly.com/chris-rogers-earthboundmartian/30-minute-landship-consult";

// Brand colors from tailwind.config.cjs / index.css (mars, alt-blk, alt-wht).
const BRAND = { mars: "#D32A46", altBlk: "#172B2D", altWht: "#F6E7CE" };
const CALENDLY_POPUP_URL = `${CALENDLY_URL}?primary_color=${BRAND.mars.slice(1)}&text_color=${BRAND.altBlk.slice(1)}&background_color=${BRAND.altWht.slice(1)}`;
const CALENDLY_CSS = "https://assets.calendly.com/assets/external/widget.css";
const CALENDLY_JS = "https://assets.calendly.com/assets/external/widget.js";

let calendlyScript: Promise<void> | null = null;

/** Loads Calendly's widget CSS and JS once (deduped across mounts). */
function loadCalendly(): Promise<void> {
  if (!document.querySelector(`link[href="${CALENDLY_CSS}"]`)) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = CALENDLY_CSS;
    document.head.appendChild(link);
  }
  if (window.Calendly) return Promise.resolve();
  if (!calendlyScript) {
    calendlyScript = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>(
        `script[src="${CALENDLY_JS}"]`
      );
      const script = existing ?? document.createElement("script");
      script.addEventListener("load", () => resolve());
      script.addEventListener("error", () => {
        calendlyScript = null;
        reject(new Error("Calendly failed to load"));
      });
      if (!existing) {
        script.src = CALENDLY_JS;
        script.async = true;
        document.body.appendChild(script);
      }
    });
  }
  return calendlyScript;
}

/** Shows Calendly's floating badge while the page is mounted. */
function useCalendlyBadge() {
  React.useEffect(() => {
    let active = true;
    loadCalendly()
      .then(() => {
        if (!active || !window.Calendly) return;
        if (document.querySelector(".calendly-badge-widget")) return;
        window.Calendly.initBadgeWidget({
          url: CALENDLY_POPUP_URL,
          text: "Schedule time with me",
          color: BRAND.mars,
          textColor: "#ffffff",
          branding: true,
        });
      })
      .catch(() => {});
    return () => {
      active = false;
      document
        .querySelectorAll(".calendly-badge-widget")
        .forEach((el) => el.remove());
    };
  }, []);
}

function openCalendly(e: React.MouseEvent<HTMLAnchorElement>) {
  e.preventDefault();
  if (window.Calendly) {
    window.Calendly.initPopupWidget({ url: CALENDLY_POPUP_URL });
  } else {
    window.open(CALENDLY_URL, "_blank", "noopener,noreferrer");
  }
}

type Topic = { title: string; intro: string; items: string[] };

const topics: Topic[] = [
  {
    title: "Websites",
    intro:
      "I built and run earthboundmartian.com and five spin-off map sites (Darkskies, Lunamalies, LotCamp, RenCamp, Forteana). Good topics:",
    items: [
      "Picking a stack (I use React, TypeScript, and Tailwind, but have experience with all sorts of stacks)",
      "Cheap, fast hosting on Azure",
      "Booking, availability calendars, and syncing calendars across rental platforms",
      "SEO basics that actually matter",
      "Putting AI agents to work in a small business",
    ],
  },
  {
    title: "Van and bus buildouts",
    intro:
      "Commodore started life as a plumber's E-350 and became a self-contained Landship that has taken guests to the Grand Canyon, the Redwoods, Colorado, and New Mexico. Serenity, a 26-ft E-450 shuttle bus, is on the bench now. Good topics:",
    items: [
      "Van, bus, or box truck: which one fits how you'll live",
      "Layout, insulation, and climate control",
      "Power and solar sizing, and water systems",
      "Weight and payload (yes, we weigh ours on a CAT scale)",
      "Turning a build into a rental",
    ],
  },
  {
    title: "Theater and film props",
    intro:
      "I've built prop machetes (there's a video of them in action), shot short-film scenes, and have woodworking pieces like Mancala boards, a marble slide, and automata boxes. It all draws on years of stage crew, carpentry, electrical, and A/V work. Good topics:",
    items: [
      "Making props on an indie budget",
      "Prop safety and storage",
      "Carpentry for sets",
      "One off prop rentals"
    ],
  },
];

const Consulting = () => {
  useCalendlyBadge();

  const structuredData: JsonLDData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: "Talk Shop With Chris — Build & Tech Consulting",
        description:
          "30-minute build and tech calls with Chris of Earthbound Martian: websites, van and bus buildouts, and theater and film props.",
        url: "https://earthboundmartian.com/consulting",
        provider: { "@id": "https://earthboundmartian.com/#organization" },
        areaServed: "US",
        offers: {
          "@type": "Offer",
          price: "75",
          priceCurrency: "USD",
          description: "30-minute call",
          url: CALENDLY_URL,
        },
      },
    ],
  };

  return (
    <Layout
      title="Talk Shop With Chris | Earthbound Martian"
      structuredData={structuredData}
    >
      <div className="max-w-4xl mx-auto px-6 py-20">
        <h1 className="text-5xl font-bold text-alt-blk mb-8">
          Talk Shop With Chris
        </h1>

        <div className="prose prose-xl text-zinc-600">
          <picture className="block mb-8 md:float-right md:ml-10 md:mb-6 w-full max-w-xs mx-auto md:mx-0 md:w-64">
            <source srcSet="/images/about/chris-rogers.webp" type="image/webp" />
            <img
              src="/images/about/chris-rogers.jpg"
              alt="Chris Rogers"
              width={600}
              height={744}
              decoding="async"
              className="w-full h-auto rounded-3xl shadow"
            />
          </picture>
          <p className="text-lg">
            I've had a few careers: stage crew, carpentry, electrical, audio and
            video design, software development, and 25 years of consulting. By
            day I'm an IT consultant, deploying AI agents and cloud solutions
            that solve real business problems. The rest of the time, Cally and I
            design, build, and rent Landships here in Richardson. All of that
            comes along on every call.
          </p>
          <p className="text-lg font-semibold text-alt-blk mt-10">
            30 minutes, $75. Bring your plans, your questions, or your mess.
            We'll work through it together.
          </p>

          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={openCalendly}
            className="inline-block bg-mars hover:bg-sunset text-white px-8 py-4 rounded-2xl font-semibold text-lg transition-all mt-4"
          >
            Book a 30-minute call
          </a>

          <h2 className="text-3xl font-bold text-alt-blk clear-both mt-16 mb-6">
            What I can help with
          </h2>
          {topics.map((t) => (
            <div key={t.title} className="mb-10">
              <h3 className="font-semibold text-2xl text-zinc-800 mb-3">
                {t.title}
              </h3>
              <p className="text-lg mb-3">{t.intro}</p>
              <ul className="list-disc pl-6 space-y-2 text-lg">
                {t.items.map((i) => (
                  <li key={i}>{i}</li>
                ))}
              </ul>
            </div>
          ))}

          <h2 className="text-3xl font-bold text-alt-blk mt-16 mb-6">
            How it works
          </h2>
          <p className="text-lg">
            Book through{" "}
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={openCalendly}
              className="text-turquoise hover:text-mars"
            >
              Calendly
            </a>
            . Pay by X Money, Venmo, or CashApp. Pick a time, tell me what
            you're working on, and come with questions. If a quick message will
            do, just{" "}
            <Link to="/contact" className="text-turquoise hover:text-mars">
              send one
            </Link>
            . I like talking shop.
          </p>

          <div className="mt-12 flex flex-col sm:flex-row sm:items-center gap-6">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={openCalendly}
              className="inline-block bg-mars hover:bg-sunset text-white px-8 py-4 rounded-2xl font-semibold text-lg transition-all"
            >
              Book a 30-minute call
            </a>
            <p className="text-xl font-medium text-zinc-800">
              Your office. Your life. Untethered.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Consulting;
